// services/collectorDay.service.js
//
// The collector's day over the API (collector-app P1-C part A) — the JSON:API
// `collector-plans` resource (backend brief 2026-09-30-collector-day-read):
//   - index: the plans I collect on today (usually one; a second batch is a
//     second resource), date then batch.
//   - show: one plan's whole day — `plan`, `cash`, `progress`, `shops[]` (each
//     with its `lines[]`) — with an `ETag`; send it back as `If-None-Match` and
//     an unchanged day answers 304 with an empty body.
//
// The contract lives in the backend, not here: read the docblocks of
// ../backend/app/Services/Commerce/CollectorDayBuilder.php and
// ../backend/app/JsonApi/V1/CollectorPlans/CollectorPlanResource.php.
//
// jsonApiClient ONLY (the resource is JSON:API, so laravel-json-api wants the
// vnd.api+json Accept header): the Sanctum cookie, X-XSRF-TOKEN and the shared
// 401/419 interceptors flow through it. Never a new axios instance — see
// src/httpConfig/config/axios.js.
import { jsonApiClient } from '@/httpConfig/config/axios.js';

const BASE = '/collector-plans';

/** A deserialized `collector-plans` row → the day document the screens read. */
function toDay(row) {
  if (!row || typeof row !== 'object') return null;
  return {
    id: row.id != null ? String(row.id) : null,
    plan: row.plan ?? null,
    cash: row.cash ?? null,
    progress: row.progress ?? null,
    shops: Array.isArray(row.shops) ? row.shops : [],
  };
}

/**
 * The plans I collect on today.
 *
 * @param {{ fields?: string }} [options] - a sparse fieldset for the index,
 *   e.g. 'plan,progress' for the light list (the full index carries every
 *   line of every plan).
 * @returns {Promise<Array<{id: string, plan: object, cash: object|null, progress: object|null, shops: Array}>>}
 */
export async function fetchCollectorPlans({ fields } = {}) {
  const params = fields ? { 'fields[collector-plans]': fields } : undefined;
  const response = await jsonApiClient.get(BASE, { params });
  const rows = response?.data?.data;
  return Array.isArray(rows) ? rows.map(toDay).filter(Boolean) : [];
}

/**
 * One plan as my day, conditionally.
 *
 * A 304 is a success here, not an error: axios rejects anything outside 2xx by
 * default, so `validateStatus` lets it through. The tag is read from the
 * response header, which a cross-origin page can see only when the backend
 * lists `ETag` in `Access-Control-Expose-Headers` (FE→BE brief
 * 2026-09-30-collector-etag-exposed-header). When it cannot be read the caller
 * simply has no tag and every refresh is a full read — the browser's own HTTP
 * cache still revalidates with the tag underneath (Laravel sends `no-cache`).
 *
 * @param {string|number} planId
 * @param {{ etag?: string|null }} [options]
 * @returns {Promise<{ notModified: boolean, etag: string|null, day: object|null }>}
 */
export async function fetchCollectorPlan(planId, { etag = null } = {}) {
  const response = await jsonApiClient.get(`${BASE}/${planId}`, {
    headers: etag ? { 'If-None-Match': etag } : {},
    validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
  });
  const received = readEtag(response);

  if (response.status === 304) {
    return { notModified: true, etag: received ?? etag, day: null };
  }

  return { notModified: false, etag: received, day: toDay(response?.data?.data) };
}

function readEtag(response) {
  const headers = response?.headers;
  if (!headers) return null;
  const value = typeof headers.get === 'function' ? headers.get('etag') : headers.etag ?? headers.ETag;
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Does the session still hold, and whose is it? A plain profile read for the
 * collector's queue (collector-app P2-B, L-D): paused on a 401 or 419, or
 * about to drop a batch on a 403. Resolves with the signed-in user's id (a
 * string, or null when the answer carries none); a 401 rejects AND reaches
 * the shared 401 handler through the interceptors — the one place that
 * decides what an expired login does.
 */
export async function probeSession() {
  const response = await jsonApiClient.get('/users/-actions/profile');
  const id = response?.data?.data?.id;
  return id == null ? null : String(id);
}
